import React from 'react';
import { Lightbulb, Target } from 'lucide-react';
import { SectionCard } from './SectionCard';
import { formatCategoryLabel } from '../utils/labels';

const SECTION_GUIDANCE = {
  operations: {
    high: 'Workflow automation, document routing, scheduling, and data re-entry are likely worth investigating.',
    medium: 'There may be targeted operational wins, especially where tools do not share data cleanly.',
    low: 'Operations may not be the first place to pilot AI unless a specific manual task is still painful.',
    clarify: 'Which repeated task would free up the most owner or manager time?'
  },
  customer: {
    high: 'Customer response, follow-up, reminders, and support triage may be strong first-pilot candidates.',
    medium: 'Look for customer moments where faster drafts or reminders help without replacing human judgment.',
    low: 'Customer-facing AI may not be the safest first pilot unless the workflow is bounded and reviewed.',
    clarify: 'Where do customers wait, repeat themselves, or fall through the cracks?'
  },
  marketing: {
    high: 'Sales follow-up, content repurposing, CRM cleanup, and campaign reporting are likely worth modeling.',
    medium: 'There may be helpful AI support in repeatable sales or content workflows.',
    low: 'Sales and marketing may be secondary unless manual follow-up or reporting is costing real time.',
    clarify: 'Which growth task is repeated every week but easy to postpone?'
  },
  team: {
    high: 'Dashboard summaries, meeting-to-task handoffs, SOP assistants, and decision support may create value.',
    medium: 'Team coordination may improve with clearer source-of-truth and reporting workflows.',
    low: 'Team/decision workflows may be less urgent than direct operational or customer bottlenecks.',
    clarify: 'What decision or handoff slows the team down most often?'
  }
};

export const SectionRecapCard = ({ sectionKey, section = {}, tools = [], integrationRating = 0 }) => {
  const score = section.total || (section.ratings || []).reduce((sum, rating) => sum + (Number(rating) || 0), 0);
  const guidance = SECTION_GUIDANCE[sectionKey] || SECTION_GUIDANCE.operations;
  const level = score >= 18 ? 'high' : score >= 10 ? 'medium' : 'low';
  const toolText = tools.length ? `${tools.length} tools selected` : 'No tools selected yet';
  const integrationText = integrationRating ? `Integration rating: ${integrationRating}/5` : 'Integration not rated yet';

  return (
    <SectionCard title={`What we’re seeing in ${formatCategoryLabel(sectionKey)}`} icon={Lightbulb} className="mt-6 border-2 border-primary/20">
      <div className="grid md:grid-cols-3 gap-4">
        <div className="rounded-lg bg-primary/5 p-4">
          <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Diagnostic signal</div>
          <div className="text-3xl font-bold text-primary mt-1">{score}/25</div>
          <p className="text-sm text-muted-foreground mt-2">High means more friction or opportunity, not poor performance.</p>
        </div>
        <div className="md:col-span-2 rounded-lg bg-white border border-muted p-4 space-y-3">
          <p className="text-sm text-foreground leading-relaxed">{guidance[level]}</p>
          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
            <span className="rounded-full bg-muted px-3 py-1">{toolText}</span>
            <span className="rounded-full bg-muted px-3 py-1">{integrationText}</span>
          </div>
          <div className="flex items-start gap-2 rounded-lg bg-accent/10 p-3 text-sm">
            <Target className="h-4 w-4 text-secondary mt-0.5" />
            <span><strong>Clarify next:</strong> {guidance.clarify}</span>
          </div>
        </div>
      </div>
    </SectionCard>
  );
};
